import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import Emittery from 'emittery';

import { ISignalRSession, ISignalRSessionEventTypes, IUserConnectedData } from '@/services/signalr/types';
import { jwt, setJwt } from '@/utils/jwt';

interface IRecheckUserConnectedRespMsg {
  jwt: string;
  refreshToken: string;
  success: boolean;
}

const defaultUserConnectedData = <IUserConnectedData> {
  companyId: '',
  connectionId: '',
  fullName: '',
  hostName: '',
  tenantKey: '',
  tenantName: '',
  userKey: '',
  userName: '',
  userRoles: [],
  sendTripsInList: 'True',
};

export class SignalrSession extends Emittery<ISignalRSessionEventTypes> implements ISignalRSession {
  private expired: boolean = false;

  private healthCheckFailures: number = 0;

  private healthCheckTimer: number | undefined;

  private hubConnection: HubConnection;

  readonly id: string;

  companyId: '';

  connectionId: '';

  fullName: '';

  hostName: '';

  tenantKey: '';

  tenantName: '';

  userKey: '';

  userName: '';

  userRoles: [];

  private readonly isHybrid: boolean;

  constructor(id: string, hubConnection: HubConnection, isHybrid: boolean = false) {
    super();
    this.id = id;
    this.hubConnection = hubConnection;
    this.isHybrid = isHybrid;
  }

  destroy() {
    clearTimeout(this.healthCheckTimer);
    this.healthCheckFailures = 0;
    this.healthCheckTimer = undefined;
    Object.assign(this, defaultUserConnectedData);
    this.expired = false;
  }

  async initialize(): Promise<ISignalRSession> {
    this.destroy();

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const data = this.isHybrid
      ? await this.hubConnection.invoke<IUserConnectedData>('UserConnected', this.id)
      : await this.hubConnection.invoke('UserConnected2', this.id, { sendTripsInList: true });

    // Request the current user data; synchronous request that returns data.
    Object.assign(this, { ...defaultUserConnectedData, ...data });

    // Start health checks.
    this.healthCheck();

    return this;
  }

  protected healthCheck() {
    // Immediately return based on the following conditions:
    if (
      // Health check already started.
      this.healthCheckTimer !== undefined ||
      // Not connected.
      this.hubConnection.state !== HubConnectionState.Connected ||
      // Session has expired.
      this.expired
    ) {
      return;
    }

    this.healthCheckTimer = <number><unknown> setTimeout(
      this.recheckUserConnected.bind(this),
      // Use a shorter timeout after first health check failure.
      this.healthCheckFailures > 0 ? 1000 : 10000,
    );
  }

  protected recheckUserConnected() {
    const currentJwt = jwt();

    const restartHealthCheck = () => {
      this.healthCheckTimer = undefined;
      this.healthCheck();
    };

    this.hubConnection
      .invoke('RecheckUserConnected', this.id, currentJwt)
      .then((data?: IRecheckUserConnectedRespMsg) => {
        // Successful, reset failure count.
        if (data?.success) {
          this.expired = false;
          this.healthCheckFailures = 0;

          // Ensure JWT is current.
          if (data.jwt && data.jwt !== currentJwt) {
            setJwt(data.jwt);

            // Local development needs updating too.
            if (import.meta.env.DEV) {
              setJwt(data.jwt, { domain: 'localhost' });
            }
          }

          return;
        }

        // Otherwise, consider it a failure and increase count.
        this.healthCheckFailures += 1;

        // If, after retrying a few times, indicate that the session has expired.
        if (this.healthCheckFailures >= 3) {
          this.expired = true;
          // False positive, returns in .then functions always resolve to promises.
          // eslint-disable-next-line consistent-return
          return this.emit('expired');
        }
      })
      .then(restartHealthCheck)
      // Intentionally do nothing. The health check failed to send which indicates a network issue
      // and redirecting to login may be premature if it really is just a network issue.
      .catch(restartHealthCheck);
  }
}
